import * as React from 'react'
import moment from 'moment'
import { TabSpeed } from '@/modules/imports/ui/FileInfoModal'
import { axiosInstance } from '@/lib/axios'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { faClose, FaIcon } from '@/lib/fa'
import { LoaderIcon } from '@/app/features/icons/Loader'
import type {
  FileDetailsDto,
  SpeedPointDto,
  VehicleSpeedDto,
} from '@/lib/api/company/fileProcessing/fileDetails/types'
import { ReadStatus } from '@/app/constants/import/readStatus'
import { CertificateStatus } from '@/app/constants/import/certificateStatus'
import { Source } from '@/app/constants/import/source'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'

type Props = {
  companyId: string | number
  vehicleId: string | number
  date: string 
  onClose: () => void
}

type RawSpeedPoint = { date: string; speed: number }
type RawSpeedInfo = {
  points?: RawSpeedPoint[]
  detailedPoints?: RawSpeedPoint[]
}


const toSpeedPerMinute = (raw: RawSpeedInfo | null): SpeedPointDto[] => {
  const detailed = raw?.detailedPoints ?? raw?.points ?? []
  if (detailed.length === 0) return []
  const byMinute = new Map<string, number[]>()
  for (const p of detailed) {
    if (p?.date == null) continue
    const minute = moment(p.date).format('YYYY-MM-DDTHH:mm')
    const arr = byMinute.get(minute)
    if (arr) arr.push(p.speed)
    else byMinute.set(minute, [p.speed])
  }
  return Array.from(byMinute.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([minute, speed]) => ({ minute, speed }))
}

const VehicleSpeedModal: React.FC<Props> = ({
  companyId,
  vehicleId,
  date,
  onClose,
}) => {
  const { t } = useLocalize()
  const [speedInfo, setSpeedInfo] = React.useState<VehicleSpeedDto | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        setIsLoading(true)
        setError(null)
     
        const anchor = moment(date)
        const startTime = anchor.clone().startOf('year').format('YYYY-MM-DD')
        const endTime = anchor
          .clone()
          .endOf('year')
          .add(1, 'day')
          .format('YYYY-MM-DD')
        const res = await axiosInstance.get(
          `api/${companyId}/speed/speedInfo?vehicleId=${vehicleId}&startTime=${startTime}&endTime=${endTime}`,
        )
        if (cancelled) return
        const speedPerMinute = toSpeedPerMinute(res.data as RawSpeedInfo)
        setSpeedInfo({
          vehicleRegistrationNumber: null,
          speedPerMinute,
        })
      } catch (err: any) {
        if (cancelled) return
        console.error('VehicleSpeedModal load error', err)
        setError(t('common.server-fetch-error'))
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [companyId, vehicleId, date, t])


  const stubFileDetails: FileDetailsDto = React.useMemo(
    () => ({
      source: Source.Card,
      readStatus: ReadStatus.Complete,
      certificateStatus: CertificateStatus.Valid,
      certificateValidityDate: null,
      isCertificateValidityDateMismatch: false,
      cardValidFrom: null,
      cardExpirationDate: null,
      firstActivityTime: null,
      lastActivityTime: null,
      downloadTime: null,
      fileName: '',
      error: null,
      tachographDetails: null,
      companyLocks: [],
      calibrations: [],
      faults: [],
      controls: [],
      signatures: [],
      fileStructureWarnings: [],
      speedInfo,
    }),
    [speedInfo],
  )

  return (
    <ModalLayout >
      <div className="tw-relative tw-flex tw-h-[90vh] tw-w-[95vw] tw-max-w-[1400px] tw-flex-col tw-rounded-lg tw-bg-white tw-shadow-2xl">
        <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-base-300 tw-p-4">
          <p className="tw-m-0 tw-text-xl tw-font-semibold tw-text-content-base">
            {t('menu.speed-chart') || 'Wykres prędkości'}
          </p>
          <FaIcon
            icon={faClose}
            className="tw-h-5 tw-w-5 tw-cursor-pointer tw-text-content-base hover:tw-text-primary"
            onClick={onClose}
          />
        </div>
        <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
          {isLoading ? (
            <div className="tw-grid tw-h-full tw-place-items-center">
              <LoaderIcon className="tw-w-12 tw-animate-spin" />
            </div>
          ) : error ? (
            <div className="tw-grid tw-h-full tw-place-items-center tw-text-sm tw-text-error">
              {error}
            </div>
          ) : (
            <TabSpeed data={stubFileDetails} />
          )}
        </div>
      </div>
    </ModalLayout>
  )
}

export default VehicleSpeedModal
